import React, { useRef, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import NavContext from "../../../context/NavContext";
import { BiMailSend } from "react-icons/bi";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";
import styles from "./InviteUser.module.scss";
import Loader from "../../UiComponents/loader/Loader";
import InputForms from "../../UiComponents/forms/InputForms";
import Axios from "../../../_services/caller.service";

const InviteForms = ({
  setReloadWaitingInvite,
  reloadWaitingInvite,
  toggle,
}) => {
  //CONTEXT
  const { t } = useTranslation();
  const { ownerId, ownerProfile, realOwnerId, realOwnerProfile, superAdmin } =
    useContext(NavContext);
  //FORMS
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  //DISPLAY
  const [loader, setLoader] = useState(false);
  const [isChecked, setIsChecked] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);

  const form = useRef();

  //PROFILE OWNER INFOS
  const senderOwner = ownerProfile.display_name;
  const emailOwner = isChecked
    ? realOwnerProfile.user_email
    : ownerProfile.user_email;

  // VALIDATION EMAIL
  const isValidEmail = (emailValue) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };

  const sendEmail = (e) => {
    e.preventDefault();

    // Trim whitespace
    const trimmedName = name.trim();
    const trimmedLastName = lastName.trim();
    const trimmedEmail = email.trim();

    // Validation
    if (!trimmedName || !trimmedLastName || !trimmedEmail) {
      toast.error(t("invite.fill_all_fields"));
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      toast.error(t("validation.invalid_email"));
      return;
    }

    if (trimmedName.length < 2) {
      toast.error(t("validation.firstname_min_2_chars"));
      return;
    }

    if (trimmedLastName.length < 2) {
      toast.error(t("validation.lastname_min_2_chars"));
      return;
    }

    setShowConfirm(true);
  };

  const handleConfirmInvite = async () => {
    const trimmedName = name.trim();
    const trimmedLastName = lastName.trim();
    const trimmedEmail = email.trim();

    form.current.reset();
    setShowConfirm(false);
    setLoader(true);

    try {
      const response = await Axios.post(`/inviteUser/${ownerId}`, {
        data: {
          name: trimmedName,
          lastName: trimmedLastName,
          email: trimmedEmail,
          ownerId,
          senderOwner,
          emailOwner,
          firstSend: true,
        },
      });

      // Handle error messages from API
      if (response.data.message === "invite already send") {
        toast.error(t("invite.already_sent"));
        setLoader(false);
        return;
      }
      if (response.data.message === "L'email saisie est déjà lié a votre compte") {
        toast.error(t("invite.email_already_linked"));
        setLoader(false);
        return;
      }
      if (response.data.message === "email blacklist") {
        toast.error(t("invite.user_blacklisted"));
        setLoader(false);
        return;
      }
      if (response.data.message === "une erreur s'est produite pendant l'envoie de l'email") {
        toast.error(t("invite.error_sending_email"));
        setLoader(false);
        return;
      }

      const hash = response.data.data.hash;
      const linkSend = response.data.data.link;

      // Create rows in table invite list
      await Axios.post(`/inviteList/${ownerId}`, {
        data: { hash, ownerId, email: trimmedEmail, lastName: trimmedLastName, name: trimmedName, linkSend },
      });

      setReloadWaitingInvite(reloadWaitingInvite + 1);
      setLoader(false);
      
      toast.success(t("invite.sent_successfully"));
      
      // Reset form
      setName("");
      setLastName("");
      setEmail("");
      
      // Close form after 2 seconds
      setTimeout(() => {
        toggle();
      }, 2000);
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'invitation:", error);
      setLoader(false);
      toast.error(t("invite.sending_error"));
    }
  };

  const handleCancelInvite = () => {
    setShowConfirm(false);
  };

  return (
    <>
      <form ref={form} className={styles.formInvite} onSubmit={sendEmail}>
        <InputForms
          type="text"
          placeholder={t("common.firstname")}
          onChange={(e) => setName(e.target.value)}
          required={true}
        />
        <InputForms
          type="text"
          placeholder={t("common.lastname")}
          onChange={(e) => setLastName(e.target.value)}
          required={true}
        />
        <InputForms
          type="email"
          placeholder={t("common.email")}
          onChange={(e) => setEmail(e.target.value)}
          required={true}
        />
        {superAdmin && (
          <div className={styles.containerCheckOwnerMail}>
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => setIsChecked(!isChecked)}
            />
            <label>
              {t("invite.response_email_label", { email: realOwnerProfile.user_email })}
            </label>
          </div>
        )}

        <button type="submit" className={styles.btnPrimary}>
          <BiMailSend style={{ marginRight: 6 }} /> {t("invite.invite_user_button")}
        </button>
        {loader && <Loader />}
      </form>

      {showConfirm && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmCard}>
            <button
              type="button"
              className={`${styles.confirmClose} has-tooltip`}
              onClick={handleCancelInvite}
              aria-label={t("common.close")}
            >
              <MdClose />
            </button>

            <h3 className={styles.confirmTitle}>{t("invite.confirm_invitation")}</h3>

            <div className={styles.confirmRecap}>
              <div className={styles.recapRow}>
                <span className={styles.recapLabel}>{t("common.firstname")}</span>
                <span className={styles.recapValue}>{name.trim()}</span>
              </div>
              <div className={styles.recapRow}>
                <span className={styles.recapLabel}>{t("common.lastname")}</span>
                <span className={styles.recapValue}>{lastName.trim()}</span>
              </div>
              <div className={styles.recapRow}>
                <span className={styles.recapLabel}>{t("common.email")}</span>
                <span className={styles.recapValue}>{email.trim()}</span>
              </div>
              <div className={styles.recapRow}>
                <span className={styles.recapLabel}>{t("invite.reply_to")}</span>
                <span className={styles.recapValue}>{emailOwner}</span>
              </div>
            </div>

            <p className={styles.confirmHint}>
              {t("invite.invitation_will_be_sent", { email: email.trim() })}
            </p>

            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.btnCancel}
                onClick={handleCancelInvite}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className={styles.btnConfirm}
                onClick={handleConfirmInvite}
              >
                <BiMailSend style={{ marginRight: 6 }} /> {t("common.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InviteForms;
